import { Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { MarketingHome } from '@/features/marketing';
import { ClaudeConnectorPage } from '@/features/marketing/ClaudeConnectorPage';
import { DurableDocumentLayerPage } from '@/features/marketing/DurableDocumentLayerPage';
import { DocumentTypePage, DocumentTypesIndexPage } from '@/features/document-types';
import { ToolDetailPage, ToolsIndexPage } from '@/features/tools';
import { appPageLabel } from '@/components/workspace-nav';
import { isAppHost } from '@/lib/host';
import { appUrl, setTabTitle } from '@/lib/brand';
import { lazyWithPreloadRecovery } from '@/lib/chunk-recovery';

function lazyComponent<TProps>(load: () => Promise<ComponentType<TProps>>) {
  return lazyWithPreloadRecovery(async () => ({ default: await load() }));
}

function lazyRoute(element: ReactNode) {
  return <Suspense fallback={null}>{element}</Suspense>;
}

const AppLayout = lazyComponent(async () => (await import('@/components/app-layout')).AppLayout);
const LinksPage = lazyComponent(async () => (
  await import('@/features/analytics/analytics-page')
).LinksPage);
const CreateLinkPage = lazyComponent(async () => (
  await import('@/features/analytics/create-link-page')
).CreateLinkPage);
const LinkSettingsPage = lazyComponent(async () => (
  await import('@/features/analytics/link-settings-page')
).LinkSettingsPage);
const DocumentsPage = lazyComponent(async () => (
  await import('@/features/documents/media-studio-page')
).MediaStudioPage);
const StudioWorkbenchPage = lazyComponent(async () => (
  await import('@/features/tools-app/studio-workbench-page')
).StudioWorkbenchPage);
const ToolsAppSlugRoute = lazyComponent(async () => (
  await import('@/features/tools-app/tools-app-slug-route')
).ToolsAppSlugRoute);
const StudioLandingPage = lazyComponent(async () => (
  await import('@/features/tools-app/studio-landing-page')
).StudioLandingPage);
const PublicHomePage = lazyComponent(async () => (
  await import('@/features/tools-app/public-home/public-home-page')
).PublicHomePage);
const ClaimInboxPage = lazyComponent(async () => (await import('@/features/inboxes/claim-inbox-page')).ClaimInboxPage);
const EnvironmentsPage = lazyComponent(async () => (
  await import('@/features/inboxes/environments-page')
).EnvironmentsPage);
const InboxHome = lazyComponent(async () => (await import('@/features/inboxes/inbox-home')).InboxHome);
const JobsPage = lazyComponent(async () => (await import('@/features/jobs/jobs-page')).JobsPage);
const LiveDocClaimPage = lazyComponent(async () => (
  await import('@/features/live-docs/live-doc-claim-page')
).LiveDocClaimPage);
const CreatePage = lazyComponent(async () => (await import('@/features/projects/create-page')).CreatePage);
const SettingsPage = lazyComponent(async () => (await import('@/features/settings/settings-page')).SettingsPage);
const AgentsIndexPage = lazyComponent(async () => (
  await import('@/features/agents/AgentsIndexPage')
).AgentsIndexPage);
const RedactAgentPage = lazyComponent(async () => (
  await import('@/features/agents/redact/RedactAgentPage')
).RedactAgentPage);
const TemplatesGalleryPage = lazyComponent(async () => (
  await import('@/features/templates/templates-gallery')
).TemplatesGalleryPage);
const TemplatePage = lazyComponent(async () => (
  await import('@/features/templates/template-page')
).TemplatePage);
const PdfAskPage = lazyComponent(async () => (
  await import('@/features/marketing/pdf-ask-page')
).PdfAskPage);
const PdfContextSamplePage = lazyComponent(async () => (
  await import('@/features/marketing/pdf-context-sample-page')
).PdfContextSamplePage);
const ExtractPage = lazyComponent(async () => (await import('@/features/solutions/ExtractPage')).ExtractPage);
const ComingSoonPage = lazyComponent(async () => (await import('@/features/solutions/ComingSoonPage')).ComingSoonPage);
const ProductPage = lazyComponent(async () => (await import('@/features/products/ProductPage')).ProductPage);
const CompareReductoPage = lazyComponent(async () => (
  await import('@/features/solutions/CompareReductoPage')
).CompareReductoPage);
const BenchmarkPage = lazyComponent(async () => (
  await import('@/features/benchmark/BenchmarkPage')
).BenchmarkPage);
const BenchmarksIndexPage = lazyComponent(async () => (
  await import('@/features/benchmark/benchmarks-index-page')
).BenchmarksIndexPage);
const ParsebenchAgenticPage = lazyComponent(async () => (
  await import('@/features/benchmark/parsebench-agentic-page')
).ParsebenchAgenticPage);
const ParsebenchLocalPage = lazyComponent(async () => (
  await import('@/features/benchmark/parsebench-local-page')
).ParsebenchLocalPage);
const LeaderboardPage = lazyComponent(async () => (
  await import('@/features/benchmark/leaderboard-page')
).LeaderboardPage);
const ModelPickerPage = lazyComponent(async () => (
  await import('@/features/models/model-picker-page')
).ModelPickerPage);
const EcosystemPage = lazyComponent(async () => (
  await import('@/features/ecosystem/EcosystemPage')
).EcosystemPage);
const PluginsIndexPage = lazyComponent(async () => (
  await import('@/features/plugins/PluginsIndexPage')
).PluginsIndexPage);
const PluginDetailPage = lazyComponent(async () => (
  await import('@/features/plugins/PluginDetailPage')
).PluginDetailPage);
const CompareLlamaparsePage = lazyComponent(async () => (
  await import('@/features/solutions/CompareLlamaparsePage')
).CompareLlamaparsePage);
const PdfToMarkdownApiPage = lazyComponent(async () => (
  await import('@/features/solutions/PdfToMarkdownApiPage')
).PdfToMarkdownApiPage);
const BankStatementExtractionPage = lazyComponent(async () => (
  await import('@/features/use-cases/BankStatementExtractionPage')
).BankStatementExtractionPage);
const BankStatementOutcomePage = lazyComponent(async () => (
  await import('@/features/bank-statement-outcome/BankStatementOutcomePage')
).BankStatementOutcomePage);
const BankStatementOutcomeSuccessPage = lazyComponent(async () => (
  await import('@/features/bank-statement-outcome/BankStatementOutcomeSuccessPage')
).BankStatementOutcomeSuccessPage);
const OkraInvoicePage = lazyComponent(async () => (
  await import('@/features/apps/OkraInvoicePage')
).OkraInvoicePage);
const DownloadPage = lazyComponent(async () => (await import('@/features/download/download-page')).DownloadPage);
const PrivacyPage = lazyComponent(async () => (await import('@/features/legal/PrivacyPage')).PrivacyPage);
const TermsPage = lazyComponent(async () => (await import('@/features/legal/TermsPage')).TermsPage);
const SupportPage = lazyComponent(async () => (await import('@/features/legal/SupportPage')).SupportPage);
const UseCasesIndexPage = lazyComponent(async () => (
  await import('@/features/use-cases/UseCasesIndexPage')
).UseCasesIndexPage);
const IndustriesIndexPage = lazyComponent(async () => (await import('@/features/for-industries/IndustriesIndexPage.js')).IndustriesIndexPage as ComponentType<object>);
const IndustryPage = lazyComponent(async () => (await import('@/features/for-industries/IndustryPage.js')).IndustryPage as ComponentType<object>);
const SignUpPage = lazyComponent(async () => (await import('@/features/auth/sign-up-page')).SignUpPage);
const SignInPage = lazyComponent(async () => (await import('@/features/auth/sign-in-page')).SignInPage);

function CreateAliasRedirect() {
  const { projectId } = useParams<{ projectId?: string }>();
  return <Navigate to={projectId ? `/spaces/${encodeURIComponent(projectId)}` : '/spaces'} replace />;
}

function ClaimRedirect() {
  const { token } = useParams<{ token: string }>();
  useEffect(() => {
    if (token) {
      window.location.replace(
        appUrl(`/claim/${encodeURIComponent(token)}`),
      );
    }
  }, [token]);
  return null;
}

function EnvironmentAliasRedirect() {
  const { id } = useParams<{ id?: string }>();
  return <Navigate to={id ? `/inboxes/${encodeURIComponent(id)}` : '/inboxes'} replace />;
}

/**
 * App-host home (W.4.105): signed-in work starts at the Studio home (/tools),
 * where a prompt, a start, or a recent document opens the editor. `?tool=`
 * deep links keep their W.4.36 contract by forwarding to /studio, the public
 * workbench.
 */
function AppHomeRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (params.has('tool')) {
    return <Navigate to={`/studio?${params.toString()}`} replace />;
  }
  const query = params.toString();
  return <Navigate to={query ? `/tools?${query}` : '/tools'} replace />;
}

/** W.4.124 R4: the Analytics tab became Links; old links keep their query. */
function AnalyticsToLinksRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/links${search}`} replace />;
}

function LegacyLibraryRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('sharing', 'published');
  return <Navigate to={`/documents?${params.toString()}`} replace />;
}

function canonicalDocumentSearch(search: string) {
  const params = new URLSearchParams(search);
  const legacyAssetId = params.get('asset');
  if (legacyAssetId && !params.has('document')) {
    params.set('document', legacyAssetId);
    params.delete('asset');
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

function AppDocumentsRedirect() {
  useEffect(() => {
    window.location.replace(`${appUrl('/documents')}${canonicalDocumentSearch(window.location.search)}`);
  }, []);
  return null;
}

function InvoiceTemplateRedirect() {
  return <Navigate to="/tables/new?template=invoices" replace />;
}

/**
 * Apex `/` (W.4.107): the Studio home shown to a visitor — the page a new
 * account opens on, where each start asks for an account. The desktop page it
 * replaced is `/download`. W.4.36 `/?tool=<id>` deep links keep working — they
 * forward to `/studio`, the free workbench.
 */
function ApexHome() {
  const tool = new URLSearchParams(window.location.search).get('tool');
  if (tool) return <Navigate to={`/studio?tool=${encodeURIComponent(tool)}`} replace />;
  return lazyRoute(<PublicHomePage />);
}

/**
 * Each app-host page names itself in the tab (W.4.132). The Worker serves the
 * apex SEO title for /tools on this host too, so without this every page keeps
 * it. Apex pages own their titles through setPageSeo; the app host is noindex.
 * An open document outranks this: the label waits until it goes (lib/brand).
 */
function useAppPageTitle() {
  const { pathname } = useLocation();
  const label = isAppHost() ? appPageLabel(pathname) : null;
  useEffect(() => {
    if (label) setTabTitle(label);
  }, [label]);
}

/**
 * Shell. Hostname-aware routing:
 *   marketing apex   → the Studio home as the landing at / (W.4.107; the free
 *                      studio workbench is at /studio, the desktop page at
 *                      /download, the classic marketing page at /about) +
 *                      public tools/docs
 *   app host         → free studio landing at / + dashboard SPA
 *                      (Clerk-gated /tables, etc.)
 *
 * One Worker, one Vite build, one asset bundle. The hostname check runs
 * in the browser (client-side SPA); the Worker's wrangler.toml route
 * pattern just decides whether this SPA is served at all.
 */
export function App() {
  useAppPageTitle();
  if (isAppHost()) {
    return (
      <Routes>
        {/* W.4.105: the app-host home is the Studio home (/tools); the free
            studio workbench stays at /studio (W.4.54 canonical path) and
            ?tool= deep links forward there. */}
        <Route path="/" element={<AppHomeRedirect />} />
        <Route path="/studio" element={lazyRoute(<StudioLandingPage />)} />
        {/* Same short entry as the apex (W.4.103); public like /studio. */}
        <Route path="/edit-pdf" element={<Navigate to="/studio?tool=edit-text" replace />} />
        <Route path="/live-docs/:sessionId/claim" element={lazyRoute(<LiveDocClaimPage />)} />
        <Route path="/sign-up/*" element={lazyRoute(<SignUpPage />)} />
        <Route path="/sign-in/*" element={lazyRoute(<SignInPage />)} />
        <Route element={lazyRoute(<AppLayout />)}>
          <Route path="/spaces" element={lazyRoute(<CreatePage />)} />
          <Route path="/spaces/:projectId" element={lazyRoute(<CreatePage />)} />
          <Route path="/create" element={<CreateAliasRedirect />} />
          <Route path="/create/:projectId" element={<CreateAliasRedirect />} />
          <Route path="/documents" element={lazyRoute(<DocumentsPage />)} />
          <Route path="/links" element={lazyRoute(<LinksPage />)} />
          <Route path="/links/new" element={lazyRoute(<CreateLinkPage />)} />
          {/* One link's settings and views (W.4.124 R6); `/settings` keeps a link named `new` clear of Create link. */}
          <Route path="/links/:namespace/settings" element={lazyRoute(<LinkSettingsPage />)} />
          <Route path="/analytics" element={<AnalyticsToLinksRedirect />} />
          <Route path="/library" element={<LegacyLibraryRedirect />} />
          {/* Files + Collections tabs disabled until ready — redirect to Documents. */}
          <Route path="/files/*" element={<Navigate to="/documents" replace />} />
          <Route path="/jobs" element={lazyRoute(<JobsPage />)} />
          <Route path="/collections/*" element={<Navigate to="/documents" replace />} />
          <Route path="/tools" element={lazyRoute(<StudioWorkbenchPage />)} />
          <Route path="/tools/:slug" element={lazyRoute(<ToolsAppSlugRoute />)} />
          <Route path="/inboxes" element={lazyRoute(<EnvironmentsPage />)} />
          <Route path="/inboxes/:id" element={lazyRoute(<EnvironmentsPage />)} />
          <Route path="/invoices" element={<InvoiceTemplateRedirect />} />
          <Route path="/environments" element={<EnvironmentAliasRedirect />} />
          <Route path="/environments/:id" element={<EnvironmentAliasRedirect />} />
          {/* Tables + Workflows tabs disabled until ready — redirect to Documents. */}
          <Route path="/tables/*" element={<Navigate to="/documents" replace />} />
          <Route path="/workflows/*" element={<Navigate to="/documents" replace />} />
          <Route path="/agents" element={lazyRoute(<AgentsIndexPage />)} />
          <Route path="/agents/redact" element={lazyRoute(<RedactAgentPage />)} />
          <Route path="/settings" element={lazyRoute(<SettingsPage />)} />
          <Route path="/settings/webhooks" element={<Navigate to="/settings?tab=webhooks" replace />} />
          <Route path="/claim/:token" element={lazyRoute(<ClaimInboxPage />)} />
          <Route path="*" element={<Navigate to="/tools" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Apex landing: the Studio home with a sign-in gate (W.4.107). The free
          studio workbench remains public at /studio; the classic marketing
          page lives on at /about. */}
      <Route path="/" element={<ApexHome />} />
      <Route path="/studio" element={lazyRoute(<StudioLandingPage />)} />
      <Route path="/about" element={<MarketingHome />} />
      <Route path="/durable-document-layer" element={<DurableDocumentLayerPage />} />
      <Route path="/add-to-claude" element={<ClaudeConnectorPage />} />
      <Route path="/ask" element={lazyRoute(<PdfAskPage />)} />
      <Route path="/ask/samples" element={lazyRoute(<PdfContextSamplePage />)} />
      <Route path="/create" element={<AppDocumentsRedirect />} />
      <Route path="/documents" element={<AppDocumentsRedirect />} />
      <Route path="/inbox" element={lazyRoute(<InboxHome />)} />
      <Route path="/settings" element={lazyRoute(<SettingsPage />)} />
      <Route path="/settings/webhooks" element={<Navigate to="/settings?tab=webhooks" replace />} />
      {/* /host (the PDF hosting workbench) retired in favor of the simpler
          /tools/pdf-to-link convention. Redirect preserves inbound links. */}
      <Route path="/host" element={<Navigate to="/tools/pdf-to-link" replace />} />
      {/* Short public entry to the browser PDF editor (W.4.103): the Studio
          opens with the edit-text tool selected, ready for a dropped PDF. */}
      <Route path="/edit-pdf" element={<Navigate to="/studio?tool=edit-text" replace />} />
      <Route path="/templates" element={lazyRoute(<TemplatesGalleryPage />)} />
      <Route path="/templates/:slug" element={lazyRoute(<TemplatePage />)} />
      <Route
        path="/benchmark"
        element={<Navigate to="/benchmarks/pdf-to-html-streaming" replace />}
      />
      <Route path="/benchmarks" element={lazyRoute(<BenchmarksIndexPage />)} />
      <Route
        path="/benchmarks/pdf-to-html-streaming"
        element={lazyRoute(<BenchmarkPage />)}
      />
      <Route
        path="/benchmarks/parsebench-agentic"
        element={lazyRoute(<ParsebenchAgenticPage />)}
      />
      <Route
        path="/benchmarks/parsebench-local"
        element={lazyRoute(<ParsebenchLocalPage />)}
      />
      <Route path="/benchmarks/:boardId" element={lazyRoute(<LeaderboardPage />)} />
      <Route path="/models" element={lazyRoute(<ModelPickerPage />)} />
      <Route path="/ecosystem" element={lazyRoute(<EcosystemPage />)} />
      <Route path="/plugins" element={lazyRoute(<PluginsIndexPage />)} />
      <Route path="/plugins/:id" element={lazyRoute(<PluginDetailPage />)} />
      <Route path="/vs/reducto" element={lazyRoute(<CompareReductoPage />)} />
      <Route path="/vs/llamaparse" element={lazyRoute(<CompareLlamaparsePage />)} />
      <Route path="/pdf-to-markdown-api" element={lazyRoute(<PdfToMarkdownApiPage />)} />
      <Route path="/pdf-to-json-api" element={<Navigate to="/parse" replace />} />
      <Route path="/extract" element={lazyRoute(<ExtractPage />)} />
      <Route path="/parse" element={lazyRoute(<ProductPage slug="parse" />)} />
      <Route path="/audit" element={lazyRoute(<ProductPage slug="audit" />)} />
      <Route path="/pdf-to-figma" element={lazyRoute(<ProductPage slug="pdf-to-figma" />)} />
      <Route path="/chrome-extension" element={lazyRoute(<ProductPage slug="chrome-extension" />)} />
      <Route path="/pdf-agents" element={lazyRoute(<ProductPage slug="pdf-agents" />)} />
      <Route path="/pdf-mcp" element={lazyRoute(<ProductPage slug="pdf-mcp" />)} />
      <Route path="/pdf-cli" element={<Navigate to="/pdf-cli-toolsets" replace />} />
      <Route path="/pdf-cli-toolsets" element={lazyRoute(<ProductPage slug="pdf-cli-toolsets" />)} />
      <Route path="/redact" element={lazyRoute(<ComingSoonPage slug="redact" />)} />
      <Route path="/document-types" element={<DocumentTypesIndexPage />} />
      <Route path="/document-types/:slug" element={<DocumentTypePage />} />
      <Route path="/tools" element={<ToolsIndexPage />} />
      <Route path="/tools/:slug" element={<ToolDetailPage />} />
      <Route path="/use-cases" element={lazyRoute(<UseCasesIndexPage />)} />
      <Route
        path="/use-cases/bank-statement-extraction"
        element={lazyRoute(<BankStatementExtractionPage />)}
      />
      <Route path="/bank-statement" element={lazyRoute(<BankStatementOutcomePage />)} />
      <Route
        path="/bank-statement/success"
        element={lazyRoute(<BankStatementOutcomeSuccessPage />)}
      />
      <Route path="/apps/okra-invoice" element={lazyRoute(<OkraInvoicePage />)} />
      <Route path="/privacy" element={lazyRoute(<PrivacyPage />)} />
      <Route path="/terms" element={lazyRoute(<TermsPage />)} />
      <Route path="/support" element={lazyRoute(<SupportPage />)} />
      <Route path="/download" element={lazyRoute(<DownloadPage />)} />
      <Route path="/downloads" element={<Navigate to="/download" replace />} />
      <Route path="/live-docs/:sessionId/claim" element={lazyRoute(<LiveDocClaimPage />)} />
      <Route path="/for" element={lazyRoute(<IndustriesIndexPage />)} />
      <Route path="/for/:slug" element={lazyRoute(<IndustryPage />)} />
      <Route path="/claim/:token" element={<ClaimRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
